import React, { useState, useEffect } from 'react';
import { Matchday, Season, Competition } from '../../types/sports';

interface MatchdayFormProps {
  matchday?: Matchday;
  seasons: Season[];
  competitions: Competition[];
  onSave: (data: {
    name: string;
    number: number;
    startDate?: string;
    endDate?: string;
    seasonId: number;
    competitionId?: number;
    isActive?: boolean;
    isCompleted?: boolean;
  }) => void;
  onCancel: () => void;
  loading?: boolean;
}

const MatchdayForm: React.FC<MatchdayFormProps> = ({
  matchday,
  seasons,
  competitions,
  onSave,
  onCancel,
  loading = false
}) => {
  const [formData, setFormData] = useState({
    name: 'Spieltag',
    number: 1,
    startDate: '',
    endDate: '',
    seasonId: 0,
    competitionId: 0,
    isActive: true,
    isCompleted: false
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (matchday) {
      setFormData({
        name: matchday.name || 'Spieltag',
        number: matchday.number || 1,
        startDate: matchday.startDate ? new Date(matchday.startDate).toISOString().split('T')[0] : '',
        endDate: matchday.endDate ? new Date(matchday.endDate).toISOString().split('T')[0] : '',
        seasonId: matchday.seasonId || 0,
        competitionId: matchday.competitionId || 0,
        isActive: matchday.isActive !== undefined ? matchday.isActive : true,
        isCompleted: matchday.isCompleted !== undefined ? matchday.isCompleted : false
      });
    }
  }, [matchday]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : 
              (name === 'seasonId' || name === 'competitionId' || name === 'number') ? 
              parseInt(value) || 0 : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name ist erforderlich';
    }

    if (formData.number < 1) {
      newErrors.number = 'Spieltag-Nummer muss mindestens 1 sein';
    }

    if (formData.seasonId === 0) {
      newErrors.seasonId = 'Saison ist erforderlich';
    }

    if (formData.startDate && formData.endDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      if (startDate >= endDate) {
        newErrors.endDate = 'Enddatum muss nach dem Startdatum liegen';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const submitData = {
        name: formData.name.trim(),
        number: formData.number,
        startDate: formData.startDate || undefined,
        endDate: formData.endDate || undefined,
        seasonId: formData.seasonId,
        competitionId: formData.competitionId || undefined,
        isActive: formData.isActive,
        isCompleted: formData.isCompleted
      };
      onSave(submitData);
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={headerStyle}>
          <h2 style={titleStyle}>
            {matchday ? 'Spieltag bearbeiten' : 'Neuer Spieltag'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} style={formStyle}>
          <div style={formGroupStyle}>
            <label style={labelStyle}>Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              disabled={loading}
              style={{
                ...inputStyle,
                ...(errors.name ? errorInputStyle : {})
              }}
              placeholder="Name des Spieltags"
            />
            {errors.name && <span style={errorTextStyle}>{errors.name}</span>}
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>Spieltag-Nummer *</label>
            <input
              type="number"
              name="number"
              value={formData.number}
              onChange={handleChange}
              disabled={loading}
              style={{
                ...inputStyle,
                ...(errors.number ? errorInputStyle : {})
              }}
              min="1"
              placeholder="1"
            />
            {errors.number && <span style={errorTextStyle}>{errors.number}</span>}
          </div>

          <div style={formRowStyle}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Startdatum</label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                disabled={loading}
                style={inputStyle}
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Enddatum</label>
              <input
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                disabled={loading}
                style={{
                  ...inputStyle,
                  ...(errors.endDate ? errorInputStyle : {})
                }}
              />
              {errors.endDate && <span style={errorTextStyle}>{errors.endDate}</span>}
            </div>
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>Saison *</label>
            <select
              name="seasonId"
              value={formData.seasonId}
              onChange={handleChange}
              disabled={loading}
              style={{
                ...selectStyle,
                ...(errors.seasonId ? errorInputStyle : {})
              }}
            >
              <option value={0}>Saison auswählen</option>
              {seasons.map(season => (
                <option key={season.id} value={season.id}>
                  {season.name} ({season.league?.name})
                </option>
              ))}
            </select>
            {errors.seasonId && <span style={errorTextStyle}>{errors.seasonId}</span>}
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>Wettbewerb</label>
            <select
              name="competitionId"
              value={formData.competitionId}
              onChange={handleChange}
              disabled={loading}
              style={selectStyle}
            >
              <option value={0}>Kein Wettbewerb</option>
              {competitions.map(competition => (
                <option key={competition.id} value={competition.id}>
                  {competition.name}
                </option>
              ))}
            </select>
          </div>

          {matchday && (
            <div style={formRowStyle}>
              <div style={checkboxGroupStyle}>
                <input
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                  disabled={loading}
                  style={{ margin: 0 }}
                />
                <label htmlFor="isActive" style={{ margin: 0, fontSize: '0.9rem' }}>
                  Aktiv
                </label>
              </div>

              <div style={checkboxGroupStyle}>
                <input
                  type="checkbox"
                  id="isCompleted"
                  name="isCompleted"
                  checked={formData.isCompleted}
                  onChange={handleChange}
                  disabled={loading}
                  style={{ margin: 0 }}
                />
                <label htmlFor="isCompleted" style={{ margin: 0, fontSize: '0.9rem' }}>
                  Abgeschlossen
                </label>
              </div>
            </div>
          )}

          <div style={formActionsStyle}>
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              style={cancelButtonStyle}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={loading}
              style={submitButtonStyle}
            >
              {loading ? 'Speichern...' : (matchday ? 'Aktualisieren' : 'Erstellen')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Styles (identical to CompetitionForm)
const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px',
  padding: '2rem',
  width: '90%',
  maxWidth: '600px',
  maxHeight: '90vh',
  overflow: 'auto',
  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
};

const headerStyle: React.CSSProperties = {
  marginBottom: '1.5rem',
  borderBottom: '1px solid #e5e7eb',
  paddingBottom: '1rem',
};

const titleStyle: React.CSSProperties = {
  fontSize: '1.5rem',
  fontWeight: 'bold',
  color: '#1f2937',
  margin: 0,
};

const formStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
};

const formGroupStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
};

const formRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '1rem',
};

const labelStyle: React.CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: '500',
  color: '#374151',
};

const inputStyle: React.CSSProperties = {
  padding: '0.75rem',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  fontSize: '1rem',
  outline: 'none',
  transition: 'border-color 0.2s',
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
};

const errorInputStyle: React.CSSProperties = {
  borderColor: '#ef4444',
};

const errorTextStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  color: '#ef4444',
  marginTop: '0.25rem',
};

const checkboxGroupStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
};

const formActionsStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '1rem',
  marginTop: '1.5rem',
  paddingTop: '1rem',
  borderTop: '1px solid #e5e7eb',
};

const cancelButtonStyle: React.CSSProperties = {
  padding: '0.75rem 1.5rem',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  backgroundColor: 'white',
  color: '#374151',
  fontSize: '1rem',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'background-color 0.2s',
};

const submitButtonStyle: React.CSSProperties = {
  padding: '0.75rem 1.5rem',
  border: 'none',
  borderRadius: '6px',
  backgroundColor: '#3b82f6',
  color: 'white',
  fontSize: '1rem',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'background-color 0.2s',
};

export default MatchdayForm;
